import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { toast } from "sonner";
import { HardHat, UserPlus, Filter } from "lucide-react";

const TRADES = ["mason", "plumber", "electrician", "foreman", "carpenter", "painter", "hvac", "helper", "other"];

export default function TalentPool() {
  const { t } = useI18n();
  const [tab, setTab] = useState("browse"); // browse | register
  const [filter, setFilter] = useState("");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const p = filter ? { trade: filter } : {};
    api.get("/candidates", { params: p }).then(({ data }) => { setList(data); setLoading(false); });
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div data-testid="talent-page">
      <section className="bg-[#0F172A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
          <div className="label-mono text-[#FFB800]">Talent Network</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-4">{t("talent.title")}</h1>
          <p className="mt-4 text-slate-300 max-w-2xl">{t("talent.subtitle")}</p>
          <div className="mt-8 flex gap-2">
            <TabBtn active={tab === "browse"} onClick={() => setTab("browse")} testid="talent-tab-browse">{t("talent.browse")}</TabBtn>
            <TabBtn active={tab === "register"} onClick={() => setTab("register")} testid="talent-tab-register">{t("talent.register")}</TabBtn>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        {tab === "browse" ? (
          <BrowseList list={list} loading={loading} filter={filter} setFilter={setFilter} />
        ) : (
          <RegisterForm onDone={() => { setTab("browse"); load(); }} />
        )}
      </section>
    </div>
  );
}

function TabBtn({ active, children, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className={`px-5 py-2.5 font-bold tracking-wide ${active ? "bg-[#FFB800] text-slate-900" : "bg-white/10 text-white hover:bg-white/20"}`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function BrowseList({ list, loading, filter, setFilter }) {
  const { t } = useI18n();
  return (
    <>
      <div className="flex flex-wrap items-center gap-3 mb-8">
        <div className="flex items-center gap-2 text-sm text-slate-500"><Filter className="w-4 h-4" /> {t("talent.filterTrade")}</div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="border border-slate-300 px-3 py-2 text-sm outline-none" data-testid="talent-filter">
          <option value="">All</option>
          {TRADES.map((tr) => <option key={tr} value={tr}>{t(`jobs.trades.${tr}`)}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-slate-500 text-center py-20">{t("common.loading")}</div>
      ) : list.length === 0 ? (
        <div className="border border-dashed border-slate-300 py-20 text-center text-slate-500" data-testid="talent-empty">{t("talent.empty")}</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((c) => (
            <div key={c.id} className="border border-slate-200 p-5 flex flex-col gap-2" data-testid={`candidate-${c.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-900 text-white flex items-center justify-center"><HardHat className="w-5 h-5" /></div>
                <div>
                  <div className="font-display font-bold">{c.full_name}</div>
                  <div className="label-mono text-slate-500">{t(`jobs.trades.${c.trade}`) || c.trade}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                <Cell label={t("talent.years")} value={`${c.years_experience}+`} />
                <Cell label={t("talent.availability")} value={t(`talent.availabilityOpts.${c.availability}`) || c.availability} />
                {c.nationality && <Cell label={t("talent.nationality")} value={c.nationality} />}
              </div>
              {c.bio && <div className="text-sm text-slate-600 mt-2 leading-relaxed line-clamp-3">{c.bio}</div>}
              <div className="text-xs text-slate-400 mt-2 italic">{t("talent.hireContact")}</div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function Cell({ label, value }) {
  return (
    <div className="border border-slate-200 px-2 py-1.5">
      <div className="label-mono text-slate-500">{label}</div>
      <div className="font-bold text-slate-800 mt-0.5">{value}</div>
    </div>
  );
}

function RegisterForm({ onDone }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", trade: "mason",
    years_experience: 0, nationality: "", availability: "immediate", bio: "",
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/candidates", { ...form, years_experience: parseInt(form.years_experience || 0) });
      toast.success(t("talent.success"));
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid md:grid-cols-2 gap-5 border border-slate-200 p-6 sm:p-8 max-w-3xl" data-testid="talent-register-form">
      <div className="md:col-span-2 flex items-center gap-3 mb-2">
        <UserPlus className="w-6 h-6 text-[#8D1B3D]" />
        <div className="font-display font-black text-xl">{t("talent.registerTitle")}</div>
      </div>
      <Field label={t("auth.name")} value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} required testid="tal-name" />
      <Field label={t("auth.email")} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="tal-email" />
      <Field label={t("auth.phone")} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required testid="tal-phone" />
      <Field label={t("talent.nationality")} value={form.nationality} onChange={(v) => setForm({ ...form, nationality: v })} testid="tal-nat" />
      <label className="block">
        <div className="label-mono text-slate-500 mb-1.5">{t("talent.trade")} *</div>
        <select value={form.trade} onChange={(e) => setForm({ ...form, trade: e.target.value })} className="w-full border border-slate-300 px-3 py-2.5 outline-none focus:border-[#8D1B3D] text-sm" data-testid="tal-trade">
          {TRADES.map((tr) => <option key={tr} value={tr}>{t(`jobs.trades.${tr}`)}</option>)}
        </select>
      </label>
      <Field label={t("talent.years")} type="number" value={form.years_experience} onChange={(v) => setForm({ ...form, years_experience: v })} required testid="tal-years" />
      <label className="block">
        <div className="label-mono text-slate-500 mb-1.5">{t("talent.availability")} *</div>
        <select value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })} className="w-full border border-slate-300 px-3 py-2.5 outline-none focus:border-[#8D1B3D] text-sm" data-testid="tal-avail">
          {Object.entries(t("talent.availabilityOpts")).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </label>
      <Field label={t("talent.bio")} value={form.bio} onChange={(v) => setForm({ ...form, bio: v })} full textarea testid="tal-bio" />
      <div className="md:col-span-2">
        <button type="submit" disabled={loading} className="btn-maroon w-full md:w-auto px-10 py-3.5 font-bold tracking-wide disabled:opacity-60" data-testid="tal-submit">
          {loading ? t("checkout.processing") : t("talent.submit")}
        </button>
      </div>
    </form>
  );
}

function Field({ label, type = "text", value, onChange, required, full, textarea, testid }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="label-mono text-slate-500 mb-1.5">{label}{required ? " *" : ""}</div>
      {textarea ? (
        <textarea value={value} required={required} onChange={(e) => onChange(e.target.value)} rows={4} className="w-full border border-slate-300 px-3 py-2.5 outline-none focus:border-[#8D1B3D] text-sm resize-none" data-testid={testid} />
      ) : (
        <input type={type} value={value} required={required} onChange={(e) => onChange(e.target.value)} className="w-full border border-slate-300 px-3 py-2.5 outline-none focus:border-[#8D1B3D] text-sm" data-testid={testid} />
      )}
    </label>
  );
}
